<template>
  <div class="banner-manage">
    <!-- 搜索工具栏 -->
    <div class="toolbar">
      <span class="search-label">自定义检索:</span>
      <label>按标题&nbsp;&nbsp;</label>
      <el-input
        v-model="searchForm.title"
        placeholder="标题名称"
        style="width: 140px; margin-right: 10px"
        clearable
      />
      
      <el-button type="primary" @click="handleSearch">搜索</el-button>
      <el-button @click="handleShowAll">全部</el-button>
      
      <el-button type="success" icon="el-icon-plus" style="float: right" @click="handleAdd">
        添加图片
      </el-button>
    </div>

    <!-- 数据表格 -->
    <el-table
      :data="tableData"
      v-loading="loading"
      border
      stripe
      style="width: 100%"
      :height="tableHeight"
    >
      <el-table-column type="index" label="序号" width="60" align="center" />
      
      <el-table-column prop="title" label="图片名称" width="180" align="center" />
      
      <el-table-column prop="image_url" show-overflow-tooltip label="图片url" width="350" align="center">
        <template slot-scope="scope">
          <a :href="scope.row.image_url" target="_blank">{{ scope.row.image_url }}</a>
        </template>
      </el-table-column>
      
      <el-table-column prop="target_url" show-overflow-tooltip label="跳转链接" width="250" align="center">
        <template slot-scope="scope">
          <a :href="scope.row.target_url" target="_blank">{{ scope.row.target_url }}</a>
        </template>
      </el-table-column>
      
      <el-table-column prop="show_type" label="平台类型" width="100" align="center">
        <template slot-scope="scope">
          {{ formatShowType(scope.row.show_type) }}
        </template>
      </el-table-column>
      
      <el-table-column prop="sort" label="排序" width="120" align="center" />
      
      <el-table-column prop="disabled" label="状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.disabled ? 'danger' : 'success'">
            {{ scope.row.disabled ? '禁用' : '启用' }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column label="操作" width="250" align="center">
        <template slot-scope="scope">
          <el-button size="mini" type="primary" @click="handleEdit(scope.row)">编辑</el-button>
          <el-button 
            v-if="scope.row.disabled" 
            size="mini" 
            type="success" 
            @click="handleToggleStatus(scope.row)"
          >
            启用
          </el-button>
          <el-button 
            v-else 
            size="mini" 
            type="warning" 
            @click="handleToggleStatus(scope.row)"
          >
            禁用
          </el-button>
          <el-button 
            v-if="scope.row.disabled" 
            size="mini" 
            type="danger" 
            @click="handleDelete(scope.row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      :current-page="pagination.page"
      :page-sizes="[10, 15, 20, 50]"
      :page-size="pagination.pageSize"
      layout="total, sizes, prev, pager, next, jumper"
      :total="pagination.total"
      style="margin-top: 20px; text-align: right"
    />

    <!-- 编辑对话框 -->
    <BannerEditDialog
      :visible.sync="editDialogVisible"
      :banner-data="currentBanner"
      :app-info="appInfo"
      @refresh="loadBannerList"
    />
  </div>
</template>

<script>
import { getBannerList, toggleBannerStatus, deleteBanner } from '@/api/custom-frontend'
import BannerEditDialog from './BannerEditDialog.vue'

export default {
  name: 'BannerManage',
  components: {
    BannerEditDialog
  },
  props: {
    appInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      tableData: [],
      searchForm: {
        title: ''
      },
      pagination: {
        page: 1,
        pageSize: 15,
        total: 0
      },
      editDialogVisible: false,
      currentBanner: null,
      tableHeight: 400
    }
  },
  mounted() {
    this.calculateTableHeight()
    window.addEventListener('resize', this.calculateTableHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateTableHeight)
  },
  watch: {
    appInfo: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadBannerList()
        }
      },
      immediate: true
    }
  },
  methods: {
    async loadBannerList() {
      if (!this.appInfo.id) return
      
      this.loading = true
      try {
        const params = {
          app_id: this.appInfo.id,
          page: this.pagination.page,
          rows: this.pagination.pageSize,
          ...this.searchForm
        }
        
        const res = await getBannerList(params)
        if (res && res.err_code === 0) {
          this.tableData = res.rows || []
          this.pagination.total = res.total || 0
        }
      } catch (error) {
        console.error('获取Banner列表失败:', error)
        this.$message.error('获取Banner列表失败')
      } finally {
        this.loading = false
      }
    },
    
    handleSearch() {
      this.pagination.page = 1
      this.loadBannerList()
    },
    
    handleShowAll() {
      this.searchForm = { title: '' }
      this.pagination.page = 1
      this.loadBannerList()
    },
    
    handleAdd() {
      this.currentBanner = null
      this.editDialogVisible = true
    },
    
    handleEdit(row) {
      this.currentBanner = { ...row }
      this.editDialogVisible = true
    },
    
    async handleToggleStatus(row) {
      const action = row.disabled ? '启用' : '禁用'
      try {
        await this.$confirm(`确认要${action}该Banner图吗？`, '确认操作', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await toggleBannerStatus({
          id: row.id,
          disabled: !row.disabled
        })
        
        if (res && res.err_code === 0) {
          this.$message.success(`${action}成功`)
          this.loadBannerList()
        } else {
          this.$message.error(res.err_msg || `${action}失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error(`${action}Banner失败:`, error)
          this.$message.error(`${action}失败`)
        }
      }
    },
    
    async handleDelete(row) {
      try {
        await this.$confirm('确认要删除该Banner图吗？', '确认删除', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await deleteBanner({ id: row.id })
        if (res && res.err_code === 0) {
          this.$message.success('删除成功')
          this.loadBannerList()
        } else {
          this.$message.error(res.err_msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除Banner失败:', error)
          this.$message.error('删除失败')
        }
      }
    },
    
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.page = 1
      this.loadBannerList()
    },
    
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadBannerList()
    },
    
    formatShowType(value) {
      const typeMap = {
        1: 'PC',
        2: '客户端',
        4: '业务员',
        8: 'H5引导图片'
      }
      return typeMap[value] || value
    },
    
    calculateTableHeight() {
      this.$nextTick(() => {
        // 计算页面高度 - 工具栏高度 - 分页组件高度 - 其他间距
        const windowHeight = window.innerHeight
        const toolbarHeight = 80  // 工具栏高度
        const paginationHeight = 60  // 分页组件高度
        const tabsHeaderHeight = 55  // 标签页头部高度
        const padding = 40  // 内边距
        
        this.tableHeight = windowHeight - toolbarHeight - paginationHeight - tabsHeaderHeight - padding
        
        // 最小高度限制
        if (this.tableHeight < 300) {
          this.tableHeight = 300
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
.banner-manage {
  padding: 20px;
  height: 100%;
  
  .toolbar {
    padding: 8px 0;
    margin-bottom: 20px;
    border-bottom: 1px solid #e6e6e6;
    
    .search-label {
      margin-right: 10px;
      font-weight: bold;
    }
    
    label {
      margin-right: 5px;
    }
    
    .el-button {
      margin-left: 5px;
    }
  }
}
</style>